/**
 * @zendir/ui - AstroIcon Component
 *
 * Wrapper around official @astrouxds/react RuxIcon component.
 * Provides all 400+ official Astro UXD icons with consistent API.
 *
 * SECURITY: Uses dynamic import with proper error handling.
 * FLEXIBILITY: Works with or without @astrouxds/react installed.
 *
 * @see https://www.astrouxds.com/components/icon-library/
 *
 * @example
 * ```tsx
 * import { AstroIcon } from '@zendir/ui/react';
 *
 * <AstroIcon name="satellite" />
 * <AstroIcon name="antenna" size="large" />
 * <AstroIcon name="thermal" color="#ff0000" />
 * ```
 */

import React, { memo, useState, useEffect, useRef } from "react";
import { useTheme } from "../../theme";
import { Icon, isValidIconName } from "./Icon";

/**
 * All official Astro UXD icon names
 * @see https://www.astrouxds.com/components/icon-library/
 */
export type AstroIconName =
  // === Astro Space/Mission Icons (Custom by Rocket Communications) ===
  | "altitude"
  | "antenna"
  | "antenna-off"
  | "antenna-receive"
  | "antenna-transmit"
  | "equipment"
  | "hardware"
  | "mission"
  | "netcom"
  | "payload"
  | "processor"
  | "processor-alt"
  | "propulsion-power"
  | "satellite-off"
  | "satellite-receive"
  | "satellite-transmit"
  | "solar"
  | "thermal"
  // === Actions ===
  | "settings"
  | "search"
  | "home"
  | "info"
  | "help"
  | "delete"
  | "edit"
  | "lock"
  | "lock-open"
  | "visibility"
  | "visibility-off"
  | "refresh"
  | "sync"
  | "download"
  | "upload"
  | "fullscreen"
  | "fullscreen-exit"
  | "zoom-in"
  | "zoom-out"
  | "schedule"
  | "timeline"
  | "today"
  | "event"
  | "bookmark"
  | "favorite"
  | "star"
  | "check-circle"
  | "done"
  | "done-all"
  | "print"
  | "open-in-new"
  | "launch"
  | "power-settings-new"
  | "history"
  | "cached"
  | "build"
  | "code"
  | "dns"
  | "language"
  | "input"
  | "label"
  | "list"
  | "track-changes"
  | "verified-user"
  | "work"
  // === Alerts ===
  | "add-alert"
  | "error"
  | "error-outline"
  | "notification-important"
  | "warning"
  // === AV ===
  | "play-arrow"
  | "pause"
  | "stop"
  | "skip-next"
  | "skip-previous"
  | "fast-forward"
  | "fast-rewind"
  | "replay"
  | "loop"
  | "shuffle"
  | "volume-up"
  | "volume-down"
  | "volume-off"
  | "volume-mute"
  | "mic"
  | "mic-off"
  | "videocam"
  | "videocam-off"
  | "movie"
  | "music-note"
  | "equalizer"
  | "library-music"
  | "queue-music"
  | "subscriptions"
  | "fiber-new"
  | "fiber-manual-record"
  | "fiber-smart-record"
  // === Communication ===
  | "call"
  | "call-end"
  | "call-made"
  | "call-received"
  | "call-missed"
  | "call-split"
  | "chat"
  | "chat-bubble"
  | "comment"
  | "contacts"
  | "dialer-sip"
  | "email"
  | "forum"
  | "import-export"
  | "live-help"
  | "location-off"
  | "location-on"
  | "message"
  | "phone"
  | "phonelink-erase"
  | "phonelink-lock"
  | "phonelink-ring"
  | "portable-wifi-off"
  | "present-to-all"
  | "ring-volume"
  | "screen-share"
  | "speaker-phone"
  | "stay-primary-landscape"
  | "stay-primary-portrait"
  | "stop-screen-share"
  | "swap-calls"
  | "textsms"
  | "voicemail"
  | "vpn-key"
  // === Content ===
  | "add"
  | "add-box"
  | "add-circle"
  | "add-circle-outline"
  | "archive"
  | "backspace"
  | "block"
  | "clear"
  | "content-copy"
  | "content-cut"
  | "content-paste"
  | "create"
  | "drafts"
  | "filter-list"
  | "flag"
  | "forward"
  | "gesture"
  | "inbox"
  | "link"
  | "link-off"
  | "low-priority"
  | "mail"
  | "markunread"
  | "move-to-inbox"
  | "next-week"
  | "redo"
  | "remove"
  | "remove-circle"
  | "remove-circle-outline"
  | "reply"
  | "reply-all"
  | "report"
  | "save"
  | "select-all"
  | "send"
  | "sort"
  | "text-format"
  | "unarchive"
  | "undo"
  | "weekend"
  // === Device ===
  | "access-alarm"
  | "access-alarms"
  | "access-time"
  | "add-alarm"
  | "airplanemode-active"
  | "airplanemode-inactive"
  | "battery-20"
  | "battery-30"
  | "battery-50"
  | "battery-60"
  | "battery-80"
  | "battery-90"
  | "battery-alert"
  | "battery-charging-20"
  | "battery-charging-30"
  | "battery-charging-50"
  | "battery-charging-60"
  | "battery-charging-80"
  | "battery-charging-90"
  | "battery-charging-full"
  | "battery-full"
  | "battery-std"
  | "battery-unknown"
  | "bluetooth"
  | "bluetooth-connected"
  | "bluetooth-disabled"
  | "bluetooth-searching"
  | "brightness-auto"
  | "brightness-high"
  | "brightness-low"
  | "brightness-medium"
  | "data-usage"
  | "developer-mode"
  | "devices"
  | "dvr"
  | "gps-fixed"
  | "gps-not-fixed"
  | "gps-off"
  | "graphic-eq"
  | "location-disabled"
  | "location-searching"
  | "network-cell"
  | "network-wifi"
  | "nfc"
  | "screen-lock-landscape"
  | "screen-lock-portrait"
  | "screen-lock-rotation"
  | "screen-rotation"
  | "sd-storage"
  | "settings-system-daydream"
  | "signal-cellular-0-bar"
  | "signal-cellular-1-bar"
  | "signal-cellular-2-bar"
  | "signal-cellular-3-bar"
  | "signal-cellular-4-bar"
  | "signal-cellular-connected-no-internet-0-bar"
  | "signal-cellular-connected-no-internet-1-bar"
  | "signal-cellular-connected-no-internet-2-bar"
  | "signal-cellular-connected-no-internet-3-bar"
  | "signal-cellular-connected-no-internet-4-bar"
  | "signal-cellular-no-sim"
  | "signal-cellular-null"
  | "signal-cellular-off"
  | "signal-wifi-0-bar"
  | "signal-wifi-1-bar"
  | "signal-wifi-1-bar-lock"
  | "signal-wifi-2-bar"
  | "signal-wifi-2-bar-lock"
  | "signal-wifi-3-bar"
  | "signal-wifi-3-bar-lock"
  | "signal-wifi-4-bar"
  | "signal-wifi-4-bar-lock"
  | "signal-wifi-off"
  | "storage"
  | "usb"
  | "wallpaper"
  | "widgets"
  | "wifi-lock"
  | "wifi-tethering"
  // === Editor ===
  | "attach-file"
  | "attach-money"
  | "border-all"
  | "border-bottom"
  | "border-clear"
  | "border-color"
  | "border-horizontal"
  | "border-inner"
  | "border-left"
  | "border-outer"
  | "border-right"
  | "border-style"
  | "border-top"
  | "border-vertical"
  | "bubble-chart"
  | "drag-handle"
  | "format-align-center"
  | "format-align-justify"
  | "format-align-left"
  | "format-align-right"
  | "format-bold"
  | "format-clear"
  | "format-color-fill"
  | "format-color-reset"
  | "format-color-text"
  | "format-indent-decrease"
  | "format-indent-increase"
  | "format-italic"
  | "format-line-spacing"
  | "format-list-bulleted"
  | "format-list-numbered"
  | "format-paint"
  | "format-quote"
  | "format-shapes"
  | "format-size"
  | "format-strikethrough"
  | "format-textdirection-l-to-r"
  | "format-textdirection-r-to-l"
  | "format-underlined"
  | "functions"
  | "highlight"
  | "insert-chart"
  | "insert-comment"
  | "insert-drive-file"
  | "insert-emoticon"
  | "insert-invitation"
  | "insert-link"
  | "insert-photo"
  | "linear-scale"
  | "merge-type"
  | "mode-comment"
  | "mode-edit"
  | "monetization-on"
  | "money-off"
  | "multiline-chart"
  | "pie-chart"
  | "pie-chart-outlined"
  | "publish"
  | "short-text"
  | "show-chart"
  | "space-bar"
  | "strikethrough-s"
  | "table-chart"
  | "text-fields"
  | "title"
  | "vertical-align-bottom"
  | "vertical-align-center"
  | "vertical-align-top"
  | "wrap-text"
  // === File ===
  | "attachment"
  | "cloud"
  | "cloud-circle"
  | "cloud-done"
  | "cloud-download"
  | "cloud-off"
  | "cloud-queue"
  | "cloud-upload"
  | "create-new-folder"
  | "file-download"
  | "file-upload"
  | "folder"
  | "folder-open"
  | "folder-shared"
  // === Hardware ===
  | "cast"
  | "cast-connected"
  | "computer"
  | "desktop-mac"
  | "desktop-windows"
  | "developer-board"
  | "device-hub"
  | "devices-other"
  | "dock"
  | "gamepad"
  | "headset"
  | "headset-mic"
  | "keyboard"
  | "keyboard-arrow-down"
  | "keyboard-arrow-left"
  | "keyboard-arrow-right"
  | "keyboard-arrow-up"
  | "keyboard-backspace"
  | "keyboard-capslock"
  | "keyboard-hide"
  | "keyboard-return"
  | "keyboard-tab"
  | "keyboard-voice"
  | "laptop"
  | "laptop-chromebook"
  | "laptop-mac"
  | "laptop-windows"
  | "memory"
  | "mouse"
  | "phone-android"
  | "phone-iphone"
  | "phonelink"
  | "phonelink-off"
  | "power-input"
  | "router"
  | "scanner"
  | "security"
  | "sim-card"
  | "smartphone"
  | "speaker"
  | "speaker-group"
  | "tablet"
  | "tablet-android"
  | "tablet-mac"
  | "toys"
  | "tv"
  | "videogame-asset"
  | "watch"
  // === Image ===
  | "add-a-photo"
  | "add-to-photos"
  | "adjust"
  | "assistant"
  | "assistant-photo"
  | "audiotrack"
  | "blur-circular"
  | "blur-linear"
  | "blur-off"
  | "blur-on"
  | "brightness-1"
  | "brightness-2"
  | "brightness-3"
  | "brightness-4"
  | "brightness-5"
  | "brightness-6"
  | "brightness-7"
  | "broken-image"
  | "brush"
  | "burst-mode"
  | "camera"
  | "camera-alt"
  | "camera-front"
  | "camera-rear"
  | "camera-roll"
  | "center-focus-strong"
  | "center-focus-weak"
  | "collections"
  | "collections-bookmark"
  | "color-lens"
  | "colorize"
  | "compare"
  | "control-point"
  | "control-point-duplicate"
  | "crop"
  | "crop-16-9"
  | "crop-3-2"
  | "crop-5-4"
  | "crop-7-5"
  | "crop-din"
  | "crop-free"
  | "crop-landscape"
  | "crop-original"
  | "crop-portrait"
  | "crop-rotate"
  | "crop-square"
  | "dehaze"
  | "details"
  | "edit"
  | "exposure"
  | "exposure-neg-1"
  | "exposure-neg-2"
  | "exposure-plus-1"
  | "exposure-plus-2"
  | "exposure-zero"
  | "filter"
  | "filter-1"
  | "filter-2"
  | "filter-3"
  | "filter-4"
  | "filter-5"
  | "filter-6"
  | "filter-7"
  | "filter-8"
  | "filter-9"
  | "filter-9-plus"
  | "filter-b-and-w"
  | "filter-center-focus"
  | "filter-drama"
  | "filter-frames"
  | "filter-hdr"
  | "filter-none"
  | "filter-tilt-shift"
  | "filter-vintage"
  | "flare"
  | "flash-auto"
  | "flash-off"
  | "flash-on"
  | "flip"
  | "gradient"
  | "grain"
  | "grid-off"
  | "grid-on"
  | "hdr-off"
  | "hdr-on"
  | "hdr-strong"
  | "hdr-weak"
  | "healing"
  | "image"
  | "image-aspect-ratio"
  | "iso"
  | "landscape"
  | "leak-add"
  | "leak-remove"
  | "lens"
  | "linked-camera"
  | "looks"
  | "looks-3"
  | "looks-4"
  | "looks-5"
  | "looks-6"
  | "looks-one"
  | "looks-two"
  | "loupe"
  | "monochrome-photos"
  | "movie-creation"
  | "movie-filter"
  | "music-note"
  | "nature"
  | "nature-people"
  | "navigate-before"
  | "navigate-next"
  | "palette"
  | "panorama"
  | "panorama-fish-eye"
  | "panorama-horizontal"
  | "panorama-vertical"
  | "panorama-wide-angle"
  | "photo"
  | "photo-album"
  | "photo-camera"
  | "photo-filter"
  | "photo-library"
  | "photo-size-select-actual"
  | "photo-size-select-large"
  | "photo-size-select-small"
  | "picture-as-pdf"
  | "portrait"
  | "remove-red-eye"
  | "rotate-90-degrees-ccw"
  | "rotate-left"
  | "rotate-right"
  | "slideshow"
  | "straighten"
  | "style"
  | "switch-camera"
  | "switch-video"
  | "tag-faces"
  | "texture"
  | "timelapse"
  | "timer"
  | "timer-10"
  | "timer-3"
  | "timer-off"
  | "tonality"
  | "transform"
  | "tune"
  | "view-comfy"
  | "view-compact"
  | "vignette"
  | "wb-auto"
  | "wb-cloudy"
  | "wb-incandescent"
  | "wb-iridescent"
  | "wb-sunny"
  // === Maps ===
  | "add-location"
  | "beenhere"
  | "directions"
  | "directions-bike"
  | "directions-boat"
  | "directions-bus"
  | "directions-car"
  | "directions-railway"
  | "directions-run"
  | "directions-subway"
  | "directions-transit"
  | "directions-walk"
  | "edit-location"
  | "ev-station"
  | "explore"
  | "flight"
  | "hotel"
  | "layers"
  | "layers-clear"
  | "local-activity"
  | "local-airport"
  | "local-atm"
  | "local-bar"
  | "local-cafe"
  | "local-car-wash"
  | "local-convenience-store"
  | "local-dining"
  | "local-drink"
  | "local-florist"
  | "local-gas-station"
  | "local-grocery-store"
  | "local-hospital"
  | "local-hotel"
  | "local-laundry-service"
  | "local-library"
  | "local-mall"
  | "local-movies"
  | "local-offer"
  | "local-parking"
  | "local-pharmacy"
  | "local-phone"
  | "local-pizza"
  | "local-play"
  | "local-post-office"
  | "local-printshop"
  | "local-see"
  | "local-shipping"
  | "local-taxi"
  | "map"
  | "my-location"
  | "navigation"
  | "near-me"
  | "person-pin"
  | "person-pin-circle"
  | "pin-drop"
  | "place"
  | "rate-review"
  | "restaurant"
  | "restaurant-menu"
  | "satellite"
  | "store-mall-directory"
  | "streetview"
  | "subway"
  | "terrain"
  | "traffic"
  | "train"
  | "tram"
  | "transfer-within-a-station"
  | "zoom-out-map"
  // === Navigation ===
  | "apps"
  | "arrow-back"
  | "arrow-downward"
  | "arrow-drop-down"
  | "arrow-drop-down-circle"
  | "arrow-drop-up"
  | "arrow-forward"
  | "arrow-upward"
  | "cancel"
  | "check"
  | "chevron-left"
  | "chevron-right"
  | "close"
  | "expand-less"
  | "expand-more"
  | "first-page"
  | "fullscreen"
  | "fullscreen-exit"
  | "last-page"
  | "menu"
  | "more-horiz"
  | "more-vert"
  | "refresh"
  | "subdirectory-arrow-left"
  | "subdirectory-arrow-right"
  | "unfold-less"
  | "unfold-more"
  // === Notification ===
  | "adb"
  | "airline-seat-flat"
  | "airline-seat-flat-angled"
  | "airline-seat-individual-suite"
  | "airline-seat-legroom-extra"
  | "airline-seat-legroom-normal"
  | "airline-seat-legroom-reduced"
  | "airline-seat-recline-extra"
  | "airline-seat-recline-normal"
  | "bluetooth-audio"
  | "confirmation-number"
  | "disc-full"
  | "do-not-disturb"
  | "do-not-disturb-alt"
  | "do-not-disturb-off"
  | "do-not-disturb-on"
  | "drive-eta"
  | "enhanced-encryption"
  | "event-available"
  | "event-busy"
  | "event-note"
  | "folder-special"
  | "live-tv"
  | "mms"
  | "more"
  | "network-check"
  | "network-locked"
  | "no-encryption"
  | "ondemand-video"
  | "personal-video"
  | "phone-bluetooth-speaker"
  | "phone-forwarded"
  | "phone-in-talk"
  | "phone-locked"
  | "phone-missed"
  | "phone-paused"
  | "power"
  | "power-off"
  | "priority-high"
  | "sd-card"
  | "sim-card-alert"
  | "sms"
  | "sms-failed"
  | "sync"
  | "sync-disabled"
  | "sync-problem"
  | "system-update"
  | "tap-and-play"
  | "time-to-leave"
  | "vibration"
  | "voice-chat"
  | "vpn-lock"
  | "wc"
  | "wifi"
  | "wifi-off"
  // === Places ===
  | "ac-unit"
  | "airport-shuttle"
  | "all-inclusive"
  | "beach-access"
  | "business-center"
  | "casino"
  | "child-care"
  | "child-friendly"
  | "fitness-center"
  | "free-breakfast"
  | "golf-course"
  | "hot-tub"
  | "kitchen"
  | "pool"
  | "room-service"
  | "rv-hookup"
  | "smoke-free"
  | "smoking-rooms"
  | "spa"
  // === Social ===
  | "cake"
  | "domain"
  | "group"
  | "group-add"
  | "location-city"
  | "mood"
  | "mood-bad"
  | "notifications"
  | "notifications-active"
  | "notifications-none"
  | "notifications-off"
  | "notifications-paused"
  | "pages"
  | "party-mode"
  | "people"
  | "people-outline"
  | "person"
  | "person-add"
  | "person-outline"
  | "plus-one"
  | "poll"
  | "public"
  | "school"
  | "sentiment-dissatisfied"
  | "sentiment-neutral"
  | "sentiment-satisfied"
  | "sentiment-very-dissatisfied"
  | "sentiment-very-satisfied"
  | "share"
  | "whatshot"
  // === Toggle ===
  | "check-box"
  | "check-box-outline-blank"
  | "indeterminate-check-box"
  | "radio-button-checked"
  | "radio-button-unchecked"
  | "star"
  | "star-border"
  | "star-half"
  // === Space Operations Aliases (mapped to existing icons) ===
  // Orbit & Trajectory
  | "orbit"
  | "trajectory"
  | "orbital"
  // Ground Operations
  | "ground-station"
  | "ground-control"
  | "dish"
  | "tracking-station"
  // Communication
  | "uplink"
  | "downlink"
  | "command"
  | "telemetry"
  | "comms"
  | "rf"
  | "link"
  | "signal"
  | "data-link"
  // Satellite States
  | "satellite"
  | "spacecraft"
  | "vehicle"
  | "sat"
  | "sc"
  // Power Systems
  | "power"
  | "battery"
  | "energy"
  | "solar-array"
  | "solar-panel"
  | "photovoltaic"
  // Propulsion
  | "thruster"
  | "engine"
  | "burn"
  | "maneuver"
  | "delta-v"
  | "rcs"
  // Thermal
  | "temperature"
  | "heat"
  | "cooling"
  | "heater"
  // Attitude & Control
  | "attitude"
  | "pointing"
  | "orientation"
  | "adcs"
  | "gyro"
  | "gyroscope"
  // Navigation
  | "gps"
  | "gnss"
  | "position"
  | "location"
  | "ephemeris"
  // Status & Health
  | "health"
  | "status"
  | "nominal"
  | "anomaly"
  | "fault"
  | "safe-mode"
  | "emergency"
  | "critical"
  | "caution"
  // Time & Scheduling
  | "pass"
  | "contact"
  | "access"
  | "window"
  | "aos"
  | "los"
  | "eclipse"
  | "sunlight"
  | "shadow"
  // Subsystems
  | "subsystem"
  | "computer"
  | "obc"
  | "cdh"
  | "flight-computer"
  | "sensor"
  | "instrument"
  // Launch & Deployment
  | "launch"
  | "rocket"
  | "liftoff"
  | "deployment"
  | "deploy"
  | "separation"
  // Operations
  | "ops"
  | "operations"
  | "control"
  | "automation"
  | "sequence"
  | "procedure"
  // Data & Storage
  | "storage"
  | "memory"
  | "recorder"
  | "playback"
  | "record"
  // Monitoring
  | "monitor"
  | "dashboard"
  | "console"
  | "display"
  // Crew
  | "astronaut"
  | "crew"
  | "eva"
  // Catch-all for any other icon (flexibility)
  | (string & {});

export type AstroIconSize = "extra-small" | "small" | "normal" | "large";

export interface AstroIconProps {
  /** Icon name from Astro UXD library */
  name: AstroIconName;
  /** Icon size preset or custom pixel value */
  size?: AstroIconSize | number;
  /** Custom color (hex, rgb, or CSS color) */
  color?: string;
  /** Accessibility label (required for screen readers) */
  label?: string;
  /** Additional CSS class */
  className?: string;
  /** Additional inline styles */
  style?: React.CSSProperties;
  /** Click handler */
  onClick?: () => void;
  /** Test ID for testing */
  "data-testid"?: string;
}

// Size mapping for fallback mode
const SIZE_MAP: Record<AstroIconSize, number> = {
  "extra-small": 16,
  small: 24,
  normal: 32,
  large: 48,
};

/**
 * Space Operations Icon Aliases
 * Maps common space terminology to existing Astro icons.
 * Provides better developer experience with intuitive naming.
 */
const ICON_ALIASES: Record<string, string> = {
  // === Orbit & Trajectory ===
  orbit: "360", // Orbital path
  trajectory: "360", // Flight path
  orbital: "360", // Orbital mechanics

  // === Ground Operations ===
  "ground-station": "antenna", // Ground station
  "ground-control": "antenna", // Mission control
  dish: "antenna", // Antenna dish
  "tracking-station": "antenna", // Tracking facility

  // === Communication ===
  uplink: "antenna-transmit", // Command upload
  downlink: "antenna-receive", // Data download
  command: "antenna-transmit", // Send command
  telemetry: "antenna-receive", // Receive telemetry
  comms: "netcom", // Communications
  rf: "antenna", // Radio frequency
  link: "netcom", // Communication link
  signal: "antenna-transmit", // Signal transmission
  "data-link": "netcom", // Data link

  // === Satellite States ===
  satellite: "satellite-transmit", // Default satellite (active)
  spacecraft: "satellite-transmit", // Spacecraft
  vehicle: "satellite-transmit", // Space vehicle
  sat: "satellite-transmit", // Abbreviation
  sc: "satellite-transmit", // Spacecraft abbreviation

  // === Power Systems ===
  power: "propulsion-power", // Power system
  battery: "battery-full", // Battery status
  energy: "propulsion-power", // Energy management
  "solar-array": "solar", // Solar arrays
  "solar-panel": "solar", // Solar panels
  photovoltaic: "solar", // PV system

  // === Propulsion ===
  thruster: "propulsion-power", // Thruster
  engine: "propulsion-power", // Engine
  burn: "propulsion-power", // Burn/maneuver
  maneuver: "propulsion-power", // Orbital maneuver
  "delta-v": "propulsion-power", // Delta-V
  rcs: "propulsion-power", // Reaction control system

  // === Thermal ===
  temperature: "thermal", // Temperature
  heat: "thermal", // Heat management
  cooling: "thermal", // Cooling system
  heater: "thermal", // Heater

  // === Attitude & Control ===
  attitude: "explore", // Attitude control
  pointing: "explore", // Pointing direction
  orientation: "explore", // Spacecraft orientation
  adcs: "explore", // Attitude determination & control
  gyro: "explore", // Gyroscope
  gyroscope: "explore", // Gyroscope

  // === Navigation ===
  gps: "gps-fixed", // GPS/GNSS
  gnss: "gps-fixed", // Global navigation
  position: "gps-fixed", // Position
  location: "place", // Location
  ephemeris: "timeline", // Ephemeris data

  // === Status & Health ===
  health: "favorite", // System health
  status: "info", // Status indicator
  nominal: "check-circle", // Nominal/OK
  anomaly: "error", // Anomaly detected
  fault: "error", // Fault condition
  "safe-mode": "warning", // Safe mode
  emergency: "warning", // Emergency
  critical: "error", // Critical status
  caution: "warning", // Caution

  // === Time & Scheduling ===
  pass: "schedule", // Satellite pass
  contact: "schedule", // Ground contact
  access: "schedule", // Access window
  window: "schedule", // Time window
  aos: "schedule", // Acquisition of signal
  los: "schedule", // Loss of signal
  eclipse: "brightness-3", // Eclipse period
  sunlight: "wb-sunny", // In sunlight
  shadow: "brightness-3", // In shadow

  // === Subsystems ===
  subsystem: "developer-board", // Generic subsystem
  computer: "memory", // Onboard computer
  obc: "memory", // Onboard computer
  cdh: "memory", // Command & data handling
  "flight-computer": "memory", // Flight computer
  sensor: "settings-input-component", // Sensor
  instrument: "settings-input-component", // Instrument

  // === Launch & Deployment ===
  launch: "flight-takeoff", // Launch
  rocket: "flight-takeoff", // Rocket
  liftoff: "flight-takeoff", // Liftoff
  deployment: "open-in-new", // Deployment
  deploy: "open-in-new", // Deploy
  separation: "call-split", // Stage separation

  // === Operations ===
  ops: "build", // Operations
  operations: "build", // Operations
  control: "tune", // Control
  automation: "autorenew", // Automation
  sequence: "playlist-play", // Command sequence
  procedure: "list", // Procedure

  // === Data & Storage ===
  storage: "sd-storage", // Data storage
  memory: "memory", // Memory
  recorder: "fiber-dvr", // Data recorder
  playback: "play-arrow", // Playback
  record: "fiber-manual-record", // Recording

  // === Monitoring ===
  monitor: "desktop-mac", // Monitoring
  dashboard: "dashboard", // Dashboard
  console: "desktop-windows", // Console
  display: "tv", // Display

  // === Crew (if applicable) ===
  astronaut: "person", // Astronaut
  crew: "group", // Crew
  eva: "directions-walk", // Extra-vehicular activity
};

/**
 * Resolve icon alias to actual Astro icon name
 */
function resolveIconAlias(name: string): string {
  return ICON_ALIASES[name] || name;
}

/**
 * Get all available icon aliases
 */
export function getIconAliases(): Record<string, string> {
  return { ...ICON_ALIASES };
}

/**
 * Check if an icon name is an alias
 */
export function isIconAlias(name: string): boolean {
  return name in ICON_ALIASES;
}

/**
 * All available Astro UXD icon names (official icons, not aliases)
 * This is the comprehensive list from the Astro UXD icon library
 */
export const ASTRO_ICON_NAMES: readonly string[] = [
  // === Astro Space/Mission Icons (Custom by Rocket Communications) ===
  "altitude",
  "antenna",
  "antenna-off",
  "antenna-receive",
  "antenna-transmit",
  "equipment",
  "hardware",
  "mission",
  "netcom",
  "payload",
  "processor",
  "processor-alt",
  "propulsion-power",
  "satellite-off",
  "satellite-receive",
  "satellite-transmit",
  "solar",
  "thermal",
  // === Actions ===
  "settings",
  "search",
  "home",
  "info",
  "help",
  "delete",
  "edit",
  "lock",
  "lock-open",
  "visibility",
  "visibility-off",
  "refresh",
  "sync",
  "download",
  "upload",
  "fullscreen",
  "fullscreen-exit",
  "zoom-in",
  "zoom-out",
  "schedule",
  "timeline",
  "today",
  "event",
  "bookmark",
  "favorite",
  "star",
  "check-circle",
  "done",
  "done-all",
  "print",
  "open-in-new",
  "launch",
  "power-settings-new",
  "history",
  "cached",
  "build",
  "code",
  "dns",
  "language",
  "input",
  "label",
  "list",
  "track-changes",
  "verified-user",
  "work",
  // === Alerts ===
  "add-alert",
  "error",
  "error-outline",
  "notification-important",
  "warning",
  // === AV ===
  "play-arrow",
  "pause",
  "stop",
  "skip-next",
  "skip-previous",
  "fast-forward",
  "fast-rewind",
  "replay",
  "loop",
  "shuffle",
  "volume-up",
  "volume-down",
  "volume-off",
  "volume-mute",
  "mic",
  "mic-off",
  "videocam",
  "videocam-off",
  "movie",
  "music-note",
  "equalizer",
  "library-music",
  "queue-music",
  // === Communication ===
  "call",
  "call-end",
  "chat",
  "chat-bubble",
  "chat-bubble-outline",
  "comment",
  "contacts",
  "email",
  "mail-outline",
  "message",
  "phone",
  "voicemail",
  "vpn-key",
  "forum",
  "live-help",
  "location-on",
  "location-off",
  "business",
  "import-contacts",
  // === Content ===
  "add",
  "remove",
  "add-circle",
  "add-circle-outline",
  "remove-circle",
  "remove-circle-outline",
  "clear",
  "content-copy",
  "content-cut",
  "content-paste",
  "create",
  "flag",
  "forward",
  "reply",
  "reply-all",
  "save",
  "save-alt",
  "select-all",
  "send",
  "undo",
  "redo",
  "filter-list",
  "sort",
  "drafts",
  "link",
  "link-off",
  // === Device ===
  "battery-full",
  "battery-charging-full",
  "battery-alert",
  "battery-unknown",
  "bluetooth",
  "bluetooth-disabled",
  "bluetooth-connected",
  "bluetooth-searching",
  "brightness-1",
  "brightness-2",
  "brightness-3",
  "brightness-4",
  "brightness-5",
  "brightness-6",
  "brightness-7",
  "brightness-auto",
  "brightness-high",
  "brightness-low",
  "brightness-medium",
  "gps-fixed",
  "gps-not-fixed",
  "gps-off",
  "graphic-eq",
  "network-cell",
  "network-wifi",
  "nfc",
  "screen-rotation",
  "sd-storage",
  "settings-system-daydream",
  "signal-cellular-4-bar",
  "signal-cellular-connected-no-internet-4-bar",
  "signal-cellular-no-sim",
  "signal-cellular-null",
  "signal-cellular-off",
  "signal-wifi-4-bar",
  "signal-wifi-4-bar-lock",
  "signal-wifi-off",
  "storage",
  "usb",
  "wifi",
  "wifi-lock",
  "wifi-off",
  "wifi-tethering",
  // === Editor ===
  "attach-file",
  "attach-money",
  "border-all",
  "border-bottom",
  "border-clear",
  "border-color",
  "border-horizontal",
  "border-inner",
  "border-left",
  "border-outer",
  "border-right",
  "border-style",
  "border-top",
  "border-vertical",
  "format-align-center",
  "format-align-justify",
  "format-align-left",
  "format-align-right",
  "format-bold",
  "format-clear",
  "format-color-fill",
  "format-color-reset",
  "format-color-text",
  "format-indent-decrease",
  "format-indent-increase",
  "format-italic",
  "format-line-spacing",
  "format-list-bulleted",
  "format-list-numbered",
  "format-paint",
  "format-quote",
  "format-size",
  "format-strikethrough",
  "format-textdirection-l-to-r",
  "format-textdirection-r-to-l",
  "format-underlined",
  "functions",
  "insert-chart",
  "insert-comment",
  "insert-drive-file",
  "insert-emoticon",
  "insert-invitation",
  "insert-link",
  "insert-photo",
  "merge-type",
  "mode-comment",
  "mode-edit",
  "publish",
  "short-text",
  "space-bar",
  "strikethrough-s",
  "text-fields",
  "title",
  "vertical-align-bottom",
  "vertical-align-center",
  "vertical-align-top",
  "wrap-text",
  // === File ===
  "attachment",
  "cloud",
  "cloud-circle",
  "cloud-done",
  "cloud-download",
  "cloud-off",
  "cloud-queue",
  "cloud-upload",
  "create-new-folder",
  "file-download",
  "file-upload",
  "folder",
  "folder-open",
  "folder-shared",
  // === Hardware ===
  "cast",
  "cast-connected",
  "computer",
  "desktop-mac",
  "desktop-windows",
  "developer-board",
  "device-hub",
  "devices-other",
  "dock",
  "gamepad",
  "headset",
  "headset-mic",
  "keyboard",
  "keyboard-arrow-down",
  "keyboard-arrow-left",
  "keyboard-arrow-right",
  "keyboard-arrow-up",
  "keyboard-backspace",
  "keyboard-capslock",
  "keyboard-hide",
  "keyboard-return",
  "keyboard-tab",
  "keyboard-voice",
  "laptop",
  "laptop-chromebook",
  "laptop-mac",
  "laptop-windows",
  "memory",
  "mouse",
  "phone-android",
  "phone-iphone",
  "phonelink",
  "phonelink-off",
  "power-input",
  "router",
  "scanner",
  "security",
  "sim-card",
  "smartphone",
  "speaker",
  "speaker-group",
  "tablet",
  "tablet-android",
  "tablet-mac",
  "toys",
  "tv",
  "watch",
  // === Image ===
  "add-a-photo",
  "add-photo-alternate",
  "add-to-photos",
  "adjust",
  "brightness-1",
  "broken-image",
  "brush",
  "burst-mode",
  "camera",
  "camera-alt",
  "camera-front",
  "camera-rear",
  "camera-roll",
  "center-focus-strong",
  "center-focus-weak",
  "collections",
  "collections-bookmark",
  "color-lens",
  "colorize",
  "compare",
  "control-point",
  "control-point-duplicate",
  "crop",
  "crop-16-9",
  "crop-3-2",
  "crop-5-4",
  "crop-7-5",
  "crop-din",
  "crop-free",
  "crop-landscape",
  "crop-original",
  "crop-portrait",
  "crop-rotate",
  "crop-square",
  "dehaze",
  "details",
  "edit",
  "exposure",
  "exposure-neg-1",
  "exposure-neg-2",
  "exposure-plus-1",
  "exposure-plus-2",
  "exposure-zero",
  "filter",
  "filter-1",
  "filter-2",
  "filter-3",
  "filter-4",
  "filter-5",
  "filter-6",
  "filter-7",
  "filter-8",
  "filter-9",
  "filter-9-plus",
  "filter-b-and-w",
  "filter-center-focus",
  "filter-drama",
  "filter-frames",
  "filter-hdr",
  "filter-none",
  "filter-tilt-shift",
  "filter-vintage",
  "flare",
  "flash-auto",
  "flash-off",
  "flash-on",
  "flip",
  "gradient",
  "grain",
  "grid-off",
  "grid-on",
  "hdr-off",
  "hdr-on",
  "hdr-strong",
  "hdr-weak",
  "healing",
  "image",
  "image-aspect-ratio",
  "image-search",
  "iso",
  "landscape",
  "leak-add",
  "leak-remove",
  "lens",
  "linked-camera",
  "looks",
  "looks-3",
  "looks-4",
  "looks-5",
  "looks-6",
  "looks-one",
  "looks-two",
  "loupe",
  "monochrome-photos",
  "movie-creation",
  "movie-filter",
  "music-off",
  "music-on",
  "navigate-before",
  "navigate-next",
  "nature",
  "nature-people",
  "palette",
  "panorama",
  "panorama-fish-eye",
  "panorama-horizontal",
  "panorama-vertical",
  "panorama-wide-angle",
  "photo",
  "photo-album",
  "photo-camera",
  "photo-filter",
  "photo-library",
  "photo-size-select-actual",
  "photo-size-select-large",
  "photo-size-select-small",
  "picture-as-pdf",
  "portrait",
  "remove-red-eye",
  "rotate-90-degrees-ccw",
  "rotate-left",
  "rotate-right",
  "slideshow",
  "straighten",
  "style",
  "switch-camera",
  "switch-video",
  "tag-faces",
  "texture",
  "timelapse",
  "timer",
  "timer-10",
  "timer-3",
  "timer-off",
  "tonality",
  "transform",
  "tune",
  "view-comfy",
  "view-compact",
  "vignette",
  "wb-auto",
  "wb-cloudy",
  "wb-incandescent",
  "wb-iridescent",
  "wb-sunny",
  // === Maps ===
  "add-location",
  "beenhere",
  "directions",
  "directions-bike",
  "directions-boat",
  "directions-bus",
  "directions-car",
  "directions-railway",
  "directions-run",
  "directions-subway",
  "directions-transit",
  "directions-walk",
  "edit-location",
  "ev-station",
  "flight",
  "hotel",
  "layers",
  "layers-clear",
  "local-activity",
  "local-airport",
  "local-atm",
  "local-bar",
  "local-cafe",
  "local-car-wash",
  "local-convenience-store",
  "local-dining",
  "local-drink",
  "local-florist",
  "local-gas-station",
  "local-grocery-store",
  "local-hospital",
  "local-hotel",
  "local-laundry-service",
  "local-library",
  "local-mall",
  "local-movies",
  "local-offer",
  "local-parking",
  "local-pharmacy",
  "local-phone",
  "local-pizza",
  "local-play",
  "local-post-office",
  "local-printshop",
  "local-see",
  "local-shipping",
  "local-taxi",
  "map",
  "my-location",
  "navigation",
  "near-me",
  "person-pin",
  "person-pin-circle",
  "pin-drop",
  "place",
  "rate-review",
  "restaurant",
  "restaurant-menu",
  "satellite",
  "store-mall-directory",
  "streetview",
  "subway",
  "terrain",
  "traffic",
  "train",
  "tram",
  "transfer-within-a-station",
  "zoom-out-map",
  // === Navigation ===
  "apps",
  "arrow-back",
  "arrow-downward",
  "arrow-drop-down",
  "arrow-drop-down-circle",
  "arrow-drop-up",
  "arrow-forward",
  "arrow-upward",
  "cancel",
  "check",
  "chevron-left",
  "chevron-right",
  "close",
  "expand-less",
  "expand-more",
  "first-page",
  "last-page",
  "menu",
  "more-horiz",
  "more-vert",
  "subdirectory-arrow-left",
  "subdirectory-arrow-right",
  "unfold-less",
  "unfold-more",
  // === Notification ===
  "adb",
  "airline-seat-flat",
  "airline-seat-flat-angled",
  "airline-seat-individual-suite",
  "airline-seat-legroom-extra",
  "airline-seat-legroom-normal",
  "airline-seat-legroom-reduced",
  "airline-seat-recline-extra",
  "airline-seat-recline-normal",
  "bluetooth-audio",
  "confirmation-number",
  "disc-full",
  "do-not-disturb",
  "do-not-disturb-alt",
  "do-not-disturb-off",
  "do-not-disturb-on",
  "drive-eta",
  "enhanced-encryption",
  "event-available",
  "event-busy",
  "event-note",
  "folder-special",
  "live-tv",
  "mms",
  "more",
  "network-check",
  "network-locked",
  "no-encryption",
  "ondemand-video",
  "personal-video",
  "phone-bluetooth-speaker",
  "phone-callback",
  "phone-forwarded",
  "phone-in-talk",
  "phone-locked",
  "phone-missed",
  "phone-paused",
  "power",
  "power-off",
  "priority-high",
  "sd-card",
  "sim-card-alert",
  "sms",
  "sms-failed",
  "sync-disabled",
  "sync-problem",
  "system-update",
  "tap-and-play",
  "time-to-leave",
  "vibration",
  "voice-chat",
  "vpn-lock",
  "wc",
  "wifi",
  "notifications",
  "notifications-active",
  "notifications-none",
  "notifications-off",
  "notifications-paused",
  // === Places ===
  "ac-unit",
  "airport-shuttle",
  "all-inclusive",
  "beach-access",
  "business-center",
  "casino",
  "child-care",
  "child-friendly",
  "fitness-center",
  "free-breakfast",
  "golf-course",
  "hot-tub",
  "kitchen",
  "meeting-room",
  "no-meeting-room",
  "pool",
  "room-service",
  "rv-hookup",
  "smoke-free",
  "smoking-rooms",
  "spa",
  // === Social ===
  "cake",
  "domain",
  "group",
  "group-add",
  "location-city",
  "mood",
  "mood-bad",
  "notifications",
  "pages",
  "party-mode",
  "people",
  "people-outline",
  "person",
  "person-add",
  "person-outline",
  "plus-one",
  "poll",
  "public",
  "school",
  "sentiment-dissatisfied",
  "sentiment-neutral",
  "sentiment-satisfied",
  "sentiment-very-dissatisfied",
  "sentiment-very-satisfied",
  "share",
  "thumb-down",
  "thumb-up",
  "whatshot",
  // === Toggle ===
  "check-box",
  "check-box-outline-blank",
  "indeterminate-check-box",
  "radio-button-checked",
  "radio-button-unchecked",
  "star",
  "star-border",
  "star-half",
  // === Additional common icons ===
  "360",
  "autorenew",
  "call-split",
  "explore",
  "extension",
  "face",
  "feedback",
  "fiber-dvr",
  "fiber-manual-record",
  "fiber-new",
  "fiber-pin",
  "fiber-smart-record",
  "get-app",
  "grade",
  "highlight-off",
  "http",
  "https",
  "important-devices",
  "lightbulb",
  "lightbulb-outline",
  "perm-camera-mic",
  "perm-contact-calendar",
  "perm-data-setting",
  "perm-device-information",
  "perm-identity",
  "perm-media",
  "perm-phone-msg",
  "perm-scan-wifi",
  "pets",
  "playlist-add",
  "playlist-add-check",
  "playlist-play",
  "pregnant-woman",
  "query-builder",
  "question-answer",
  "receipt",
  "record-voice-over",
  "redeem",
  "report-problem",
  "restore",
  "restore-page",
  "room",
  "rounded-corner",
  "rowing",
  "settings-applications",
  "settings-backup-restore",
  "settings-bluetooth",
  "settings-brightness",
  "settings-cell",
  "settings-ethernet",
  "settings-input-antenna",
  "settings-input-component",
  "settings-input-composite",
  "settings-input-hdmi",
  "settings-input-svideo",
  "settings-overscan",
  "settings-phone",
  "settings-power",
  "settings-remote",
  "settings-voice",
  "shop",
  "shop-two",
  "shopping-basket",
  "shopping-cart",
  "speaker-notes",
  "speaker-notes-off",
  "spellcheck",
  "stars",
  "subject",
  "supervisor-account",
  "swap-horiz",
  "swap-vert",
  "swap-vertical-circle",
  "system-update-alt",
  "tab",
  "tab-unselected",
  "theaters",
  "thumb-down",
  "thumb-up",
  "thumbs-up-down",
  "toc",
  "toll",
  "touch-app",
  "translate",
  "trending-down",
  "trending-flat",
  "trending-up",
  "turned-in",
  "turned-in-not",
  "update",
  "view-agenda",
  "view-array",
  "view-carousel",
  "view-column",
  "view-day",
  "view-headline",
  "view-list",
  "view-module",
  "view-quilt",
  "view-stream",
  "view-week",
  "visibility",
  "visibility-off",
  "watch-later",
  "youtube-searched-for",
  "aspect-ratio",
  "assignment",
  "assignment-ind",
  "assignment-late",
  "assignment-return",
  "assignment-returned",
  "assignment-turned-in",
  "backup",
  "book",
  "flight-takeoff",
  "flight-land",
] as const;

/**
 * Get all available Astro icon names
 */
export function getAstroIconNames(): readonly string[] {
  return ASTRO_ICON_NAMES;
}

/**
 * Get all available icon names including aliases
 */
export function getAllIconNames(): {
  icons: readonly string[];
  aliases: Record<string, string>;
} {
  return {
    icons: ASTRO_ICON_NAMES,
    aliases: ICON_ALIASES,
  };
}

// Global state for RuxIcon loading with subscription pattern
type LoadState = "pending" | "loading" | "loaded" | "failed";
let loadState: LoadState = "pending";
let RuxIconComponent: React.ComponentType<any> | null = null;
const subscribers = new Set<() => void>();

function subscribe(callback: () => void) {
  subscribers.add(callback);
  return () => subscribers.delete(callback);
}

function notifySubscribers() {
  subscribers.forEach((cb) => cb());
}

/**
 * Safely load RuxIcon from @astrouxds/react
 * Returns null if not available
 */
async function loadRuxIcon(): Promise<React.ComponentType<any> | null> {
  if (loadState === "loaded") {
    return RuxIconComponent;
  }

  if (loadState === "failed") {
    return null;
  }

  if (loadState === "loading") {
    // Wait for the existing load to complete
    return new Promise((resolve) => {
      const unsub = subscribe(() => {
        unsub();
        resolve(RuxIconComponent);
      });
    });
  }

  loadState = "loading";

  try {
    // Dynamic import for tree-shaking and optional dependency
    const astroModule = await import("@astrouxds/react");
    RuxIconComponent = astroModule.RuxIcon;
    loadState = "loaded";
    notifySubscribers();
    return RuxIconComponent;
  } catch (err) {
    loadState = "failed";
    notifySubscribers();
    return null;
  }
}

/**
 * Fallback icon component when @astrouxds/react is not installed.
 * Uses built-in Icon SVGs when the name matches, otherwise shows a generic placeholder.
 */
function FallbackIcon({
  name,
  size,
  color,
  label,
  className,
  style,
  onClick,
  "data-testid": testId,
}: AstroIconProps) {
  const { tokens } = useTheme();
  const actualSize =
    typeof size === "number" ? size : SIZE_MAP[size || "normal"];

  if (isValidIconName(name)) {
    return (
      <Icon
        name={name}
        size={actualSize}
        color={color}
        className={className}
        aria-label={label || name}
        onClick={onClick}
        style={style}
      />
    );
  }

  return (
    <span
      role="img"
      aria-label={label || name}
      className={className}
      data-testid={testId}
      onClick={onClick}
      tabIndex={onClick ? 0 : undefined}
      onKeyDown={onClick ? (e) => e.key === "Enter" && onClick() : undefined}
      style={{
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        width: actualSize,
        height: actualSize,
        fontSize: actualSize * 0.4,
        fontFamily: tokens.typography.fontFamily.mono,
        fontWeight: 700,
        color: color || tokens.colors.status.off,
        backgroundColor: tokens.colors.background.surface,
        border: `1px solid ${tokens.colors.border.default}`,
        borderRadius: 4,
        cursor: onClick ? "pointer" : "default",
        userSelect: "none",
        ...style,
      }}
      title={`Icon: ${name} (install @astrouxds/react for official icons)`}
    >
      <svg
        width={actualSize * 0.6}
        height={actualSize * 0.6}
        viewBox="0 0 24 24"
        fill="currentColor"
      >
        <rect
          x="3"
          y="3"
          width="18"
          height="18"
          rx="2"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.5"
        />
        <circle cx="12" cy="12" r="2" fill="currentColor" />
      </svg>
    </span>
  );
}

/**
 * AstroIcon - Official Astro UXD Icons
 *
 * Displays icons from the official Astro UX Design System.
 * Automatically uses @astrouxds/react if installed, otherwise shows fallback.
 *
 * @example
 * ```tsx
 * // Basic usage
 * <AstroIcon name="satellite" />
 *
 * // With size
 * <AstroIcon name="antenna" size="large" />
 * <AstroIcon name="thermal" size={48} />
 *
 * // With color
 * <AstroIcon name="warning" color="var(--zendir-status-caution)" />
 *
 * // With click handler
 * <AstroIcon name="settings" onClick={() => openSettings()} />
 * ```
 */
export const AstroIcon = memo(function AstroIcon(props: AstroIconProps) {
  const {
    name,
    size = "normal",
    color,
    label,
    className,
    style,
    onClick,
    "data-testid": testId,
  } = props;

  const [, forceUpdate] = useState(0);
  const [currentState, setCurrentState] = useState(loadState);
  const mountedRef = useRef(true);

  // Subscribe to load state changes and trigger loading
  useEffect(() => {
    mountedRef.current = true;

    // Subscribe to state changes
    const unsubscribe = subscribe(() => {
      if (mountedRef.current) {
        setCurrentState(loadState);
        forceUpdate((n) => n + 1);
      }
    });

    // Trigger load if not yet started
    if (loadState === "pending") {
      loadRuxIcon();
    }

    return () => {
      mountedRef.current = false;
      unsubscribe();
    };
  }, []);

  // If still loading, show a subtle placeholder
  if (currentState === "pending" || currentState === "loading") {
    const actualSize = typeof size === "number" ? size : SIZE_MAP[size];
    return (
      <span
        style={{
          display: "inline-block",
          width: actualSize,
          height: actualSize,
          ...style,
        }}
        className={className}
        aria-hidden="true"
      />
    );
  }

  // Resolve icon alias to actual Astro icon name
  const resolvedName = resolveIconAlias(name);

  // If @astrouxds/react is available, use official RuxIcon
  if (RuxIconComponent) {
    const RuxIcon = RuxIconComponent;
    const pixelSize = typeof size === "number" ? size : SIZE_MAP[size];

    // Map numeric size to closest Astro size preset for RuxIcon
    // This helps RuxIcon render at approximately the right size before CSS kicks in
    const getClosestAstroSize = (px: number): AstroIconSize => {
      if (px <= 18) return "extra-small"; // 16px
      if (px <= 28) return "small"; // 24px
      if (px <= 40) return "normal"; // 32px
      return "large"; // 48px
    };
    const astroSize =
      typeof size === "number" ? getClosestAstroSize(size) : size;

    // Default to off/grey color if no color specified
    const iconColor = color || "var(--zendir-status-off, #a4abb6)";

    return (
      <span
        role="img"
        aria-label={label || name}
        style={{
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          width: pixelSize,
          height: pixelSize,
          minWidth: pixelSize,
          minHeight: pixelSize,
          flexShrink: 0,
          // Don't use overflow:hidden - it clips the icon SVG
          ...style,
        }}
        className={className}
      >
        <RuxIcon
          icon={resolvedName}
          size={astroSize}
          label={label || name}
          data-testid={testId}
          onClick={onClick}
          style={
            {
              // Force the RuxIcon to respect our exact pixel size
              "--iconDefaultSize": `${pixelSize}px`,
              "--iconSize": `${pixelSize}px`,
              "--size": `${pixelSize}px`,
              color: iconColor,
              width: `${pixelSize}px`,
              height: `${pixelSize}px`,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              ...(onClick && { cursor: "pointer" }),
            } as React.CSSProperties
          }
        />
      </span>
    );
  }

  // Fallback when @astrouxds/react is not installed
  return <FallbackIcon {...props} name={resolvedName} />;
});

/**
 * Check if @astrouxds/react is available
 * Useful for conditional rendering or feature detection
 */
export function isAstroIconsAvailable(): boolean {
  return loadState === "loaded" && RuxIconComponent !== null;
}

/**
 * Preload the Astro icons module
 * Call this early in your app to ensure icons are ready
 */
export async function preloadAstroIcons(): Promise<boolean> {
  const component = await loadRuxIcon();
  return component !== null;
}

export default AstroIcon;
